import { type MouseEvent, memo, type ReactNode, useEffect } from 'react';
import { type BackgroundProps } from 'react-imgix';

import { type Board } from '@air/api/types';
import { ImgixImage, type ImgixImageProps } from '@air/component-imgix-image';
import { cn } from '@air/utils-tailwind';

import { RiveThumbnail } from '~/components/RiveThumbnail';
import { getCloudFrontUrlFromImgixUrl } from '~/constants/cloudfront';

export type BoardThumbnailProps = {
  className?: string;
  board: Pick<Board, 'thumbnails' | 'title'>;
  borderRadius?: number;
  children?: ReactNode;
  imgixHeight?: number;
  imgixWidth?: number;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  onFallbackChange?: (isFallback: boolean) => void;
  /** This is the img "sizes" attribute that should be used to determine the board thumbnail sizes */
  sizes: ImgixImageProps['sizes'];
  width: string | number;
  height: string | number;
  objectFit?: 'contain' | 'cover' | 'fill' | 'scale-down' | 'none' | 'initial' | 'inherit';
  imgixParams?: BackgroundProps['imgixParams'];
};

/**
 * This is used to display board's thumbnail (only image)
 */
const BoardThumbnail = memo(
  ({
    board: { thumbnails, title },
    children,
    sizes,
    width,
    height,
    borderRadius = 4,
    onClick,
    imgixHeight = 400,
    imgixParams,
    imgixWidth = 400,
    className,
    objectFit = 'contain',
    onFallbackChange,
  }: BoardThumbnailProps) => {
    const thumbnail = thumbnails?.length ? thumbnails[0] : null;
    const isBase64Image = thumbnail?.startsWith('data:image');
    const isRiveFile = thumbnail?.toLowerCase().endsWith('.riv');
    const fallbackUrl = thumbnail && getCloudFrontUrlFromImgixUrl(thumbnail);
    const shouldShowFallback = !isBase64Image && !isRiveFile && (!fallbackUrl || !thumbnail);

    useEffect(() => {
      onFallbackChange?.(shouldShowFallback);
    }, [shouldShowFallback, onFallbackChange]);

    return (
      <div
        data-testid="BOARD_CARD_THUMBNAIL"
        data-title={title}
        className={cn(
          'relative overflow-hidden [&>img]:relative [&>img]:h-full [&>img]:w-full',
          onClick ? 'cursor-pointer' : 'cursor-default',
          className,
        )}
        style={{
          cursor: onClick ? 'pointer' : 'default',
          width,
          height,
          minWidth: width,
          borderRadius,
        }}
        onClick={onClick}
      >
        {isBase64Image && thumbnail ? (
          <img src={thumbnail} alt={title} style={{ objectFit }} />
        ) : isRiveFile && thumbnail ? (
          <RiveThumbnail src={thumbnail} width={Number(width)} height={Number(height)} borderRadius={borderRadius} />
        ) : fallbackUrl && thumbnail ? (
          <ImgixImage
            alt={title}
            fallbackUrl={fallbackUrl}
            height={imgixHeight}
            imgixParams={{
              w: 400,
              h: 400,
              fit: 'contain',
              cs: 'origin',
              ...imgixParams,
            }}
            sizes={sizes}
            src={thumbnail}
            style={{ objectFit }}
            width={imgixWidth}
          />
        ) : (
          <div className="size-full bg-grey-4" />
        )}
        {children}
      </div>
    );
  },
);

BoardThumbnail.displayName = 'BoardThumbnail';

export default BoardThumbnail;
